import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, VpnKey, Check, Close } from '@mui/icons-material';
import Swal from 'sweetalert2';

// Componente para forzar el cambio de contraseña en el primer inicio de sesión
function RefreshPasswordOnFirstLogin() {
  const navigate = useNavigate();
  const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ newPassword: false, confirmPassword: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [strength, setStrength] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    passwordsMatch: false
  });

  // Verificar existencia del token de primer inicio
  useEffect(() => {
    const token = localStorage.getItem('refreshPasswordToken');
    if (!token) {
      Swal.fire({
        icon: 'error',
        title: 'Acceso denegado',
        text: 'No se encontró autorización para el cambio de contraseña del primer inicio de sesión.',
        confirmButtonText: 'Ir a inicio',
      }).then(() => navigate('/home'));
    }
  }, [navigate]);

  const updateStrength = (npw, cpw) => {
    setStrength({
      minLength: npw.length >= 8,
      hasUppercase: /[A-Z]/.test(npw),
      hasLowercase: /[a-z]/.test(npw),
      hasNumber: /\d/.test(npw),
      passwordsMatch: npw === cpw && cpw.length > 0
    });
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setPasswords(prev => ({ ...prev, [field]: value }));
    const npw = field === 'newPassword' ? value : passwords.newPassword;
    const cpw = field === 'confirmPassword' ? value : passwords.confirmPassword;
    updateStrength(npw, cpw);
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!passwords.newPassword.trim()) newErrors.newPassword = 'La nueva contraseña es requerida';
    else if (!(strength.minLength && strength.hasUppercase && strength.hasLowercase && strength.hasNumber)) newErrors.newPassword = 'No cumple los requisitos';
    if (!passwords.confirmPassword.trim()) newErrors.confirmPassword = 'Debe confirmar la contraseña';
    else if (!strength.passwordsMatch) newErrors.confirmPassword = 'Las contraseñas no coinciden';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const token = localStorage.getItem('refreshPasswordToken');
    if (!token) {
      Swal.fire({ icon: 'error', title: 'Token no disponible', text: 'Vuelva a iniciar sesión.' });
      return navigate('/sign-in');
    }
    setLoading(true);
    try {
      Swal.fire({ title: 'Actualizando contraseña...', allowOutsideClick: false, allowEscapeKey: false, didOpen: () => Swal.showLoading() });
      const response = await fetch('https://dprincess-training.servehttp.com/user/first-login/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ newPassword: passwords.newPassword })
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        Swal.close();
        // Limpiar token temporal
        localStorage.removeItem('refreshPasswordToken');
        await Swal.fire({
          icon: 'success',
          title: 'Contraseña actualizada',
            text: 'Inicie sesión con su nueva contraseña.',
          confirmButtonText: 'Continuar'
        }).then(() => navigate('/sign-in'));
      } else if (response.status === 403) {
        Swal.close();
        Swal.fire({ icon: 'error', title: 'Acceso denegado', text: 'No tiene permiso para realizar esta acción.' });
      } else if (response.status === 401) {
        Swal.close();
        Swal.fire({ icon: 'error', title: 'No autorizado', text: 'Su sesión ha expirado. Inicie sesión nuevamente.' }).then(() => {
          localStorage.removeItem('refreshPasswordToken');
          navigate('/sign-in');
        });
      } else {
        Swal.close();
        Swal.fire({ icon: 'error', title: 'Error', text: data.message || 'No se pudo actualizar la contraseña.' });
      }
    } catch (err) {
      console.error('Error cambiando contraseña primer inicio:', err);
      Swal.close();
      Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'No se pudo conectar con el servidor.' });
    } finally {
      setLoading(false);
    }
  };

  const reqMetCount = [strength.minLength, strength.hasUppercase, strength.hasLowercase, strength.hasNumber].filter(Boolean).length;

  return (
    <Container component="main" maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
      <Paper elevation={8} sx={{ p:4, display:'flex', flexDirection:'column', alignItems:'center', borderRadius:3 }}>
        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', mb:3 }}>
          <Box sx={{ bgcolor:'#4a148c', borderRadius:'50%', p:2, mb:2 }}>
            <VpnKey sx={{ fontSize:40, color:'#fff' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight:'bold', color:'#4a148c', textAlign:'center' }}>Cambiar Contraseña</Typography>
          <Typography variant="body2" sx={{ mt:1, color:'#666', textAlign:'center' }}>Ingrese y confirme su nueva contraseña para continuar.</Typography>
        </Box>
        <Box component="form" onSubmit={handleSubmit} sx={{ width:'100%' }}>
          <TextField
            fullWidth
            required
            margin="normal"
            label="Nueva Contraseña"
            type={show.newPassword ? 'text' : 'password'}
            value={passwords.newPassword}
            onChange={handleChange('newPassword')}
            error={!!errors.newPassword}
            helperText={errors.newPassword}
            disabled={loading}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock sx={{ color:'#4a148c' }}/></InputAdornment>,
              endAdornment: <InputAdornment position="end"><IconButton onClick={()=>setShow(p=>({...p,newPassword:!p.newPassword}))} edge="end" disabled={loading}>{show.newPassword? <VisibilityOff/>:<Visibility/>}</IconButton></InputAdornment>
            }}
          />
          <TextField
            fullWidth
            required
            margin="normal"
            label="Confirmar Nueva Contraseña"
            type={show.confirmPassword ? 'text' : 'password'}
            value={passwords.confirmPassword}
            onChange={handleChange('confirmPassword')}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            disabled={loading}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Lock sx={{ color:'#4a148c' }}/></InputAdornment>,
              endAdornment: <InputAdornment position="end"><IconButton onClick={()=>setShow(p=>({...p,confirmPassword:!p.confirmPassword}))} edge="end" disabled={loading}>{show.confirmPassword? <VisibilityOff/>:<Visibility/>}</IconButton></InputAdornment>
            }}
          />

          {passwords.newPassword && (
            <Box sx={{ mt:2 }}>
              <Typography variant="body2" sx={{ mb:1, fontWeight:500, color:'#4a148c' }}>Requisitos:</Typography>
              <Box sx={{ display:'flex', flexDirection:'column', gap:0.5 }}>
                <Req ok={strength.minLength} text="Al menos 8 caracteres" />
                <Req ok={strength.hasUppercase} text="Una letra mayúscula" />
                <Req ok={strength.hasLowercase} text="Una letra minúscula" />
                <Req ok={strength.hasNumber} text="Un número" />
                {passwords.confirmPassword && <Req ok={strength.passwordsMatch} text="Coinciden" />}
              </Box>
              <Box sx={{ mt:2 }}>
                <Typography variant="caption" sx={{ display:'block', mb:0.5 }}>Fortaleza:</Typography>
                <Box sx={{ display:'flex', gap:0.5 }}>
                  {[1,2,3,4].map(i => (
                    <Box key={i} sx={{ flex:1, height:6, borderRadius:3, backgroundColor: reqMetCount >= i ? (reqMetCount <=1 ? '#f44336' : reqMetCount===2 ? '#ff9800' : reqMetCount===3 ? '#2196f3' : '#4caf50') : '#e0e0e0' }} />
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          <Alert severity="info" sx={{ mt:3, bgcolor:'rgba(74,20,140,0.05)' }}>
            Debe incluir mínimo 8 caracteres, una mayúscula, una minúscula y un número.
          </Alert>

          <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ mt:3, mb:2, py:1.5, fontWeight:'bold', backgroundColor:'#4a148c', '&:hover':{ backgroundColor:'#6a1b9a' } }}>
            {loading ? <Box sx={{ display:'flex', alignItems:'center', gap:1 }}><CircularProgress size={20} color="inherit"/> Guardando...</Box> : 'Guardar Nueva Contraseña'}
          </Button>
          <Button fullWidth variant="outlined" disabled={loading} onClick={()=>navigate('/sign-in')} sx={{ py:1.2, borderColor:'#4a148c', color:'#4a148c', '&:hover':{ borderColor:'#6a1b9a', backgroundColor:'rgba(74,20,140,0.04)' } }}>
            Cancelar
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

// Sub-componente para renderizar un requisito
function Req({ ok, text }) {
  return (
    <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
      {ok ? <Check sx={{ fontSize:16, color:'#4caf50' }}/> : <Close sx={{ fontSize:16, color:'#f44336' }}/>} 
      <Typography variant="body2" sx={{ fontSize:'0.8rem', color: ok ? '#4caf50' : '#f44336' }}>{text}</Typography>
    </Box>
  );
}

export default RefreshPasswordOnFirstLogin;
